import { type as JobsiteType } from '../api/generated';
import { JobsiteDTO } from '../api/generated';

export function isDemoLive(jobsite: JobsiteDTO): boolean {
  const liveFeature = jobsite.features?.['live'] as { demo?: boolean } | undefined;
  return liveFeature?.demo === true;
}

export function isLiveEnabled(jobsite: JobsiteDTO): boolean {
  const liveFeature = jobsite.features?.['live'] as { enabled?: boolean } | undefined;
  return liveFeature?.enabled !== false;
}

export function isDocumentationEnabled(jobsite: JobsiteDTO): boolean {
  const documentationFeature = jobsite.features?.['documentation'] as { enabled?: boolean } | undefined;
  return documentationFeature?.enabled !== false;
}

const PENDING_JOBSITE_REDIRECT_TOKEN_KEY = 'pendingJobsiteRedirectToken';

export function createPendingJobsiteRedirect(): string {
  const token = `${Date.now()}:${Math.random()}`;
  sessionStorage.setItem(PENDING_JOBSITE_REDIRECT_TOKEN_KEY, token);
  return token;
}

export function isPendingJobsiteRedirect(token: string): boolean {
  return sessionStorage.getItem(PENDING_JOBSITE_REDIRECT_TOKEN_KEY) === token;
}

export function clearPendingJobsiteRedirect(token?: string): void {
  if (token && !isPendingJobsiteRedirect(token)) {
    return;
  }
  sessionStorage.removeItem(PENDING_JOBSITE_REDIRECT_TOKEN_KEY);
}

const JOBSITE_TYPE_DEFAULTS: Record<JobsiteType, string> = {
  truck: '/documentation',
  train: '/reports',
  heap: '/heap-recordings',
  belt: '/observations',
};

/**
 * Determines the best route to navigate to after jobsite selection
 * @param previousJobsite - The previously selected jobsite
 * @param newJobsite - The newly selected jobsite
 * @param lastVisitedRoute - The last route the user visited before jobsite selection
 * @returns The route to navigate to
 */
export function getSmartNavigationRoute(
  previousJobsite: JobsiteDTO | undefined,
  newJobsite: JobsiteDTO,
  lastVisitedRoute: string | null
): string {
  // If no previous jobsite or same type, try to return to last visited route
  if (!previousJobsite || previousJobsite.type === newJobsite.type) {
    if (lastVisitedRoute) {
      // Verify the route is valid for the jobsite
      if (isRouteValidForJobsite(lastVisitedRoute, newJobsite)) {
        return lastVisitedRoute;
      }
    }
  }

  // Different jobsite type or no valid last route - use type-specific default
  return getDefaultRouteForJobsite(newJobsite);
}

function getDefaultRouteForJobsite(jobsite: JobsiteDTO): string {
  const truckDefault = isDocumentationEnabled(jobsite) ? '/documentation' : '/reports';
  if (jobsite.type === 'truck') {
    return truckDefault;
  }
  return JOBSITE_TYPE_DEFAULTS[jobsite.type] || truckDefault;
}

function isRouteValidForJobsite(route: string, jobsite: JobsiteDTO): boolean {
  switch (jobsite.type) {
    // Trains don't have documentation or live-prediction
    case 'train':
      return ['/documentation', '/live-prediction'].every((x) => x !== route);
    // Heaps have heap-recordings; heap-live is only valid when live is not disabled
    case 'heap': {
      if (isLiveEnabled(jobsite)) {
        return ['/heap-live', '/heap-recordings'].includes(route);
      }
      return route === '/heap-recordings';
    }
    // documentation is only valid when it is not disabled
    case 'truck':
    default:
      if (route === '/documentation') {
        return isDocumentationEnabled(jobsite);
      }
      return true;
  }
}
